import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Device-local memory of recipes whose live scrape came back empty-handed.
 *
 * The detail screen re-scrapes to HEAL cosmetic gaps — a missing intro
 * paragraph, ingredients stored without quantities, a click-tracking source
 * URL. Those heals are gated on `lastScrapedAt`, which only a SUCCESSFUL scrape
 * writes. So a recipe whose site can never be scraped (Le Devoir publishes its
 * recipes as `ReportageNewsArticle` with no machine-readable recipe anywhere on
 * the page) re-runs the whole direct → Googlebot → headless chain on EVERY open,
 * forever, burning an AI-quota credit each time to chase a description that is
 * never coming.
 *
 * Remembering the attempt closes that loop: a failed heal now waits out the
 * same 24h a successful one does. Deliberately NOT consulted when the recipe is
 * missing its actual CONTENT — that is a dead end the user can see, and it is
 * worth retrying every time in case the site recovered.
 *
 * Local and disposable by design: this is a throttle, not data. Losing it costs
 * one redundant scrape.
 */

const KEY = 'paprika_scrape_failures';

/** Matches the `staleEnoughToRetry` window in `shouldScrapeRecipe`. */
export const SCRAPE_BACKOFF_MS = 24 * 60 * 60 * 1000;

type FailureMap = Record<string, string>; // recipeId -> ISO timestamp

/**
 * Hydrated once, then kept in sync by every write. The gate is consulted on
 * each recipe open, and the answer is almost always "no entry" — paying an
 * AsyncStorage round trip for that would put storage latency on the render path
 * of every recipe in the app.
 */
let cache: FailureMap | null = null;
let hydrating: Promise<FailureMap> | null = null;

/** Drop entries that can no longer suppress anything, so the map stays small. */
const prune = (map: FailureMap, now: number): FailureMap => {
  const fresh: FailureMap = {};
  for (const [id, at] of Object.entries(map)) {
    const ts = new Date(at).getTime();
    if (Number.isFinite(ts) && now - ts < SCRAPE_BACKOFF_MS) fresh[id] = at;
  }
  return fresh;
};

const load = async (): Promise<FailureMap> => {
  if (cache) return cache;
  if (hydrating) return hydrating;

  hydrating = (async () => {
    try {
      const raw = await AsyncStorage.getItem(KEY);
      const parsed = raw ? (JSON.parse(raw) as FailureMap) : {};
      cache = prune(parsed && typeof parsed === 'object' ? parsed : {}, Date.now());
    } catch {
      // Unreadable storage must not block the recipe from opening.
      cache = {};
    } finally {
      hydrating = null;
    }
    return cache!;
  })();

  return hydrating;
};

const save = async (map: FailureMap): Promise<void> => {
  cache = map;
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // The in-memory copy still throttles for the rest of this session.
  }
};

/** ISO timestamp of the last failed scrape for this recipe, if still recent. */
export async function getLastFailedScrapeAt(recipeId: string): Promise<string | undefined> {
  if (!recipeId) return undefined;
  const map = await load();
  const at = map[recipeId];
  if (!at) return undefined;
  const ts = new Date(at).getTime();
  if (!Number.isFinite(ts) || Date.now() - ts >= SCRAPE_BACKOFF_MS) return undefined;
  return at;
}

export async function recordFailedScrape(recipeId: string, now: number = Date.now()): Promise<void> {
  if (!recipeId) return;
  const map = await load();
  await save({ ...prune(map, now), [recipeId]: new Date(now).toISOString() });
}

/**
 * Forget a recipe's failures. Called on a successful scrape so a site that
 * recovered is not held back by an entry from before it did.
 */
export async function clearFailedScrape(recipeId: string): Promise<void> {
  if (!recipeId) return;
  const map = await load();
  if (!(recipeId in map)) return;
  const { [recipeId]: _dropped, ...rest } = map;
  await save(rest);
}

/** Test seam — drops the in-memory copy so the next read re-hydrates. */
export function __resetScrapeBackoffCache(): void {
  cache = null;
  hydrating = null;
}
